// UpgradePrompt — shared modal shown when a Free tier user hits a limit.
//
// v0.38.0 — initial. Stripe checkout isn't wired yet (waiting for
//           InSite-ID cutover + TenantProducts maintenance webhook), so
//           the "Get Pro" button currently shows a "Coming soon" state
//           and bumps an `onContact` callback for the parent to handle
//           (typically a mailto to sales@droppin.app or an inline form).
// v0.45.19 — "Get Pro" now routes to /upgrade?reason=<key> (a real
//            comparison page with the full Free vs Pro feature matrix)
//            instead of firing a JS alert. The screen handles the
//            mailto / Stripe handoff so the modal stays minimal.
//
// Props:
//   open       — boolean, render when true
//   onClose    — fires on backdrop click or × button
//   reason     — string key from tier.limitMessage() (e.g. 'activeProjects')
//   onContact  — DEPRECATED. Legacy override; now ignored unless set.

import React from 'react';
import { useNavigate } from 'react-router';
import { theme, btnPrimary, btnSecondary } from '../theme';
import { limitMessage } from '../utils/tier';

export default function UpgradePrompt({ open, onClose, reason, onContact }) {
  const navigate = useNavigate();
  if (!open) return null;
  const msg = limitMessage(reason);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, width: 460, maxWidth: '100%',
          borderRadius: 12,
          padding: '24px 26px',
          boxShadow: '0 10px 30px rgba(15,23,42,0.25)',
          position: 'relative',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            background: `linear-gradient(135deg, ${theme.brand} 0%, #ef4444 100%)`,
            color: 'white',
            fontSize: 11, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            padding: '3px 8px', borderRadius: 999,
            marginBottom: 12,
          }}
        >Upgrade to Pro</span>

        <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800, color: theme.text }}>
          You've hit a Free-tier limit
        </h2>

        <p style={{ margin: '0 0 14px', fontSize: 14, color: theme.textLight, lineHeight: 1.5 }}>
          {msg}
        </p>

        <div style={{
          background: theme.bg,
          border: `1px solid ${theme.brand}`,
          borderRadius: 8,
          padding: '12px 14px',
          fontSize: 13,
          color: theme.text,
          marginBottom: 16,
        }}>
          <strong style={{ display: 'block', marginBottom: 4 }}>DropPin Pro</strong>
          Unlimited projects · unlimited defects · public share links · white-label PDFs ·
          10GB photo storage · team members.
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{ ...btnSecondary, padding: '8px 14px' }}
          >Maybe later</button>
          <button
            type="button"
            onClick={() => {
              // v0.45.19 — close the modal, then route to the full
              // comparison page. Legacy onContact override still
              // honoured for any caller that wants to intercept (none
              // do at the moment).
              if (typeof onContact === 'function') { onContact(); return; }
              if (typeof onClose === 'function') onClose();
              navigate(`/upgrade${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);
            }}
            style={{
              ...btnPrimary,
              padding: '8px 16px',
              background: `linear-gradient(135deg, ${theme.brand} 0%, #ef4444 100%)`,
              border: 'none',
              fontWeight: 700,
            }}
          >Compare plans</button>
        </div>

        <button
          type="button"
          onClick={onClose}
          title="Close"
          style={{
            position: 'absolute', top: 10, right: 12,
            width: 28, height: 28, borderRadius: '50%',
            border: 'none', background: 'transparent',
            color: theme.textLight, fontSize: 18, cursor: 'pointer',
            lineHeight: 1,
          }}
        >×</button>
      </div>
    </div>
  );
}
